import { useForm, usePage } from "@inertiajs/react"
import { DirectUpload } from "@rails/activestorage"
import { FileVideo, Hash, Image, Send, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import type { Hashtag } from "@/types/serializers"
import type RequestsCreatePost from "@/types/serializers/RequestsCreatePost"

interface UserSuggestion {
  id: number
  first_name: string
  last_name: string
  full_name: string
  nickname: string
  avatar_url: string | null
  position: string | null
}

interface MediaAttachment {
  media_url: string
  media_type: string
  file: File
  previewUrl: string | null
  uploading: boolean
  progress: number
}

const POSITION_LABELS: Record<string, string> = {
  portero: "Portero",
  defensa_central: "Defensa Central",
  lateral_derecho: "Lateral Derecho",
  lateral_izquierdo: "Lateral Izquierdo",
  mediocampista_central: "Mediocampista Central",
  mediocampista_ofensivo: "Mediocampista Ofensivo",
  mediocampista_defensivo: "Mediocampista Defensivo",
  extremo_derecho: "Extremo Derecho",
  extremo_izquierdo: "Extremo Izquierdo",
  delantero_centro: "Delantero Centro",
  segundo_delantero: "Segundo Delantero",
}

const VISIBILITY_LABELS: Record<string, string> = {
  publico: "Público",
  seguidores: "Seguidores",
  privado: "Privado",
}

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"]
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"]
const ALL_MEDIA_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES]
const IMAGE_MAX_SIZE = 50 * 1024 * 1024 // 50MB
const VIDEO_MAX_SIZE = 500 * 1024 * 1024 // 500MB
const MAX_IMAGES = 4
const MAX_VIDEOS = 1

type SuggestionMode = "hashtag" | "mention" | null

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / 1024).toFixed(0)} KB`
}

function isImageType(type: string): boolean {
  return IMAGE_TYPES.includes(type)
}

export default function PostComposer() {
  const { data, setData, post, processing, reset } =
    useForm<RequestsCreatePost>({
      post: {
        body: "",
        visibility: "publico",
        post_media_attributes: [],
      },
    })

  const { auth } = usePage<{
    auth: {
      user: {
        id: number
        full_name: string
        first_name: string
        last_name: string
        avatar_url: string | null
      }
    }
  }>().props

  const [hashtagSuggestions, setHashtags] = useState<Hashtag[]>([])
  const [userSuggestions, setUserSuggestions] = useState<UserSuggestion[]>([])
  const [suggestionMode, setSuggestionMode] = useState<SuggestionMode>(null)
  const [mediaAttachments, setMediaAttachments] = useState<MediaAttachment[]>(
    [],
  )
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchHashtags = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      void fetch(`/hashtags?q=${encodeURIComponent(query)}`)
        .then((res) => res.json())
        .then((data: Hashtag[]) => {
          setHashtags(data)
          setSuggestionMode(data.length > 0 ? "hashtag" : null)
        })
    }, 300)
  }, [])

  const fetchUserSuggestions = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      void fetch(`/users/search?q=${encodeURIComponent(query)}`)
        .then((res) => res.json())
        .then((data: UserSuggestion[]) => {
          setUserSuggestions(data)
          setSuggestionMode(data.length > 0 ? "mention" : null)
        })
    }, 300)
  }, [])

  const handleBodyChange = (value: string) => {
    setData("post", { ...data.post, body: value })

    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = value.slice(0, cursorPos)

    // Check for @mention pattern first
    const mentionMatch = /@(\w+)$/u.exec(textBeforeCursor)
    if (mentionMatch && mentionMatch[1].length >= 2) {
      fetchUserSuggestions(mentionMatch[1])
      return
    }

    // Check for #hashtag pattern
    const hashtagMatch = /#(\w+)$/u.exec(textBeforeCursor)
    if (hashtagMatch && hashtagMatch[1].length >= 2) {
      fetchHashtags(hashtagMatch[1])
      return
    }

    setSuggestionMode(null)
    setHashtags([])
    setUserSuggestions([])
  }

  const insertHashtag = (name: string) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = data.post.body.slice(0, cursorPos)
    const textAfterCursor = data.post.body.slice(cursorPos)
    const hashtagStart = textBeforeCursor.lastIndexOf("#")

    if (hashtagStart >= 0) {
      const newBody =
        textBeforeCursor.slice(0, hashtagStart) + `#${name} ` + textAfterCursor
      setData("post", { ...data.post, body: newBody })
    }

    setSuggestionMode(null)
    setHashtags([])
    textarea.focus()
  }

  const insertMention = (nickname: string) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = data.post.body.slice(0, cursorPos)
    const textAfterCursor = data.post.body.slice(cursorPos)
    const mentionStart = textBeforeCursor.lastIndexOf("@")

    if (mentionStart >= 0) {
      const newBody =
        textBeforeCursor.slice(0, mentionStart) +
        `@${nickname} ` +
        textAfterCursor
      setData("post", { ...data.post, body: newBody })
    }

    setSuggestionMode(null)
    setUserSuggestions([])
    textarea.focus()
  }

  const validateFile = (file: File): string | null => {
    if (!ALL_MEDIA_TYPES.includes(file.type)) {
      return "Tipo de archivo no permitido"
    }

    const isImage = isImageType(file.type)
    const maxSize = isImage ? IMAGE_MAX_SIZE : VIDEO_MAX_SIZE

    if (file.size > maxSize) {
      return `Archivo demasiado grande (máx. ${formatFileSize(maxSize)})`
    }

    // Check limits
    const isVideo = !isImage
    const currentImages = mediaAttachments.filter(
      (m) => m.media_type === "image",
    ).length
    const currentVideos = mediaAttachments.filter(
      (m) => m.media_type === "video",
    ).length

    if (isImage && currentImages >= MAX_IMAGES) {
      return `Máximo ${MAX_IMAGES} imágenes permitidas`
    }
    if (isVideo && currentVideos >= MAX_VIDEOS) {
      return `Máximo ${MAX_VIDEOS} video permitido`
    }
    // If adding a video and there are already images, or vice versa with video existing
    if (isVideo && currentImages > 0) {
      return "No se pueden mezclar imágenes y videos"
    }
    if (isImage && currentVideos > 0) {
      return "No se pueden mezclar imágenes y videos"
    }

    return null
  }

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setMediaError(validationError)
      return
    }

    setMediaError(null)
    const isImage = isImageType(file.type)
    const mediaType = isImage ? "image" : "video"
    const previewUrl = isImage ? URL.createObjectURL(file) : null

    const newAttachment: MediaAttachment = {
      media_url: "",
      media_type: mediaType,
      file,
      previewUrl,
      uploading: true,
      progress: 0,
    }

    const attachmentIndex = mediaAttachments.length
    setMediaAttachments((prev) => [...prev, newAttachment])

    const upload = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads",
      {
        directUploadWillStoreFileWithXHR(xhr: XMLHttpRequest) {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percent = Math.round((event.loaded / event.total) * 100)
              setMediaAttachments((prev) =>
                prev.map((a, i) =>
                  i === attachmentIndex ? { ...a, progress: percent } : a,
                ),
              )
            }
          })
        },
      },
    )

    upload.create((error, blob) => {
      if (error) {
        setMediaAttachments((prev) =>
          prev.filter((_, i) => i !== attachmentIndex),
        )
        setMediaError("Error al subir el archivo")
        return
      }
      if (blob) {
        setMediaAttachments((prev) =>
          prev.map((a, i) =>
            i === attachmentIndex
              ? {
                  ...a,
                  media_url: blob.signed_id,
                  uploading: false,
                  progress: 100,
                }
              : a,
          ),
        )
        // Update form data
        setData("post", {
          ...data.post,
          post_media_attributes: [
            ...data.post.post_media_attributes,
            { media_url: blob.signed_id, media_type: mediaType },
          ],
        })
      }
    })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const removeAttachment = (index: number) => {
    const attachment = mediaAttachments[index]
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl)
    }
    setMediaAttachments((prev) => prev.filter((_, i) => i !== index))
    setData("post", {
      ...data.post,
      post_media_attributes: data.post.post_media_attributes.filter(
        (_, i) => i !== index,
      ),
    })
  }

  const hasContent =
    data.post.body.trim() || data.post.post_media_attributes.length > 0
  const anyUploading = mediaAttachments.some((a) => a.uploading)

  const handlePublishClick = (e: React.FormEvent) => {
    e.preventDefault()
    setShowPreview(true)
  }

  const handleConfirmPublish = () => {
    setShowPreview(false)
    post("/publicaciones", {
      onSuccess: () => {
        reset()
        setMediaAttachments([])
        setMediaError(null)
      },
    })
  }

  return (
    <>
      <form
        onSubmit={handlePublishClick}
        className="border-border bg-card mb-6 rounded-lg border p-4"
      >
        <div className="relative">
          <Textarea
            ref={textareaRef}
            placeholder="¿Qué querés compartir?"
            value={data.post.body}
            onChange={(e) => handleBodyChange(e.target.value)}
            className="text-foreground placeholder:text-krown-grey-50 mb-3 min-h-[80px] resize-none border-0 bg-transparent p-0 text-sm focus-visible:ring-0"
          />

          {/* Hashtag suggestions dropdown */}
          {suggestionMode === "hashtag" && hashtagSuggestions.length > 0 && (
            <div
              className="border-border bg-krown-grey-85 absolute top-full left-0 z-50 mt-1 max-h-48 w-64 overflow-y-auto rounded-lg border py-1 shadow-lg"
              role="listbox"
              aria-label="Sugerencias de hashtags"
            >
              {hashtagSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  role="option"
                  onClick={() => insertHashtag(suggestion.name)}
                  className="text-foreground hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                >
                  <Hash className="text-krown-300 h-3.5 w-3.5" />
                  <span className="font-medium">#{suggestion.name}</span>
                  <span className="text-muted-foreground ml-auto text-xs">
                    {suggestion.posts_count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* User mention suggestions dropdown */}
          {suggestionMode === "mention" && userSuggestions.length > 0 && (
            <div
              className="border-border bg-card absolute top-full left-0 z-50 mt-1 max-h-60 w-80 overflow-y-auto rounded-lg border py-1 shadow-lg"
              role="listbox"
              aria-label="Sugerencias de usuarios"
            >
              {userSuggestions.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  role="option"
                  onClick={() => insertMention(user.nickname)}
                  className="text-foreground hover:bg-secondary flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage
                      src={user.avatar_url ?? undefined}
                      alt={user.full_name}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-krown-200 text-xs font-bold text-white">
                      {user.first_name[0]}
                      {user.last_name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <span className="truncate font-medium">
                        {user.full_name}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        @{user.nickname}
                      </span>
                    </div>
                    {user.position && (
                      <span className="text-krown-300 text-xs">
                        {POSITION_LABELS[user.position] ?? user.position}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Media previews */}
        {mediaAttachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {mediaAttachments.map((attachment, index) => (
              <div
                key={index}
                className="border-border bg-secondary/50 relative rounded-lg border p-1"
              >
                {attachment.media_type === "image" && attachment.previewUrl ? (
                  <img
                    src={attachment.previewUrl}
                    alt={attachment.file.name}
                    className="h-20 w-20 rounded object-cover"
                  />
                ) : (
                  <div className="bg-secondary flex h-20 w-20 flex-col items-center justify-center rounded">
                    <FileVideo className="text-muted-foreground h-8 w-8" />
                    <span className="text-muted-foreground mt-1 max-w-[72px] truncate text-[10px]">
                      {attachment.file.name}
                    </span>
                  </div>
                )}

                {/* Upload progress overlay */}
                {attachment.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded bg-black/60">
                    <span className="text-xs font-medium text-white">
                      {attachment.progress}%
                    </span>
                  </div>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
                  aria-label="Eliminar archivo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Media error */}
        {mediaError && (
          <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
            <p className="text-xs text-red-400">{mediaError}</p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALL_MEDIA_TYPES.join(",")}
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div className="border-border flex items-center justify-between border-t pt-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-krown-grey-50 hover:text-krown-accent transition-colors"
              aria-label="Adjuntar archivo"
            >
              <Image className="h-5 w-5" />
            </button>
            <select
              value={data.post.visibility}
              onChange={(e) =>
                setData("post", { ...data.post, visibility: e.target.value })
              }
              className="border-krown-grey-65 bg-krown-grey-85 text-muted-foreground focus:border-krown-200 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-wider uppercase outline-none"
            >
              <option value="publico">Público</option>
              <option value="seguidores">Seguidores</option>
              <option value="privado">Privado</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={processing || !hasContent || anyUploading}
            className="from-krown-orange-from to-krown-orange-to shadow-krown-orange-from/20 hover:shadow-krown-orange-from/40 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r px-5 py-2 text-xs font-bold tracking-wider text-white uppercase shadow-lg transition-all disabled:opacity-40 disabled:shadow-none"
          >
            <Send className="h-3.5 w-3.5" />
            Publicar
          </button>
        </div>
      </form>

      {/* Confirmation preview modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Vista previa de la publicación</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* User info */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={auth.user.avatar_url ?? undefined}
                  alt={auth.user.full_name}
                  className="object-cover"
                />
                <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
                  {auth.user.first_name[0]}
                  {auth.user.last_name[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-foreground text-sm font-semibold">
                  {auth.user.full_name}
                </p>
                <span className="bg-secondary text-muted-foreground inline-block rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wider uppercase">
                  {VISIBILITY_LABELS[data.post.visibility] ??
                    data.post.visibility}
                </span>
              </div>
            </div>

            {/* Post body */}
            {data.post.body && (
              <p className="text-krown-grey-30 text-sm">{data.post.body}</p>
            )}

            {/* Media preview */}
            {mediaAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 overflow-hidden rounded-lg">
                {mediaAttachments.map((attachment, index) =>
                  attachment.media_type === "image" && attachment.previewUrl ? (
                    <img
                      key={index}
                      src={attachment.previewUrl}
                      alt={attachment.file.name}
                      className="max-h-64 w-full rounded-lg object-cover"
                    />
                  ) : (
                    <div
                      key={index}
                      className="bg-secondary flex w-full items-center gap-3 rounded-lg p-4"
                    >
                      <FileVideo className="text-muted-foreground h-10 w-10" />
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {attachment.file.name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatFileSize(attachment.file.size)}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="border-border text-muted-foreground hover:bg-secondary hover:text-foreground rounded-full border px-5 py-2 text-xs font-bold tracking-wider uppercase transition-colors"
            >
              Volver a editar
            </button>
            <button
              type="button"
              onClick={handleConfirmPublish}
              disabled={processing}
              className="from-krown-orange-from to-krown-orange-to shadow-krown-orange-from/20 hover:shadow-krown-orange-from/40 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r px-5 py-2 text-xs font-bold tracking-wider text-white uppercase shadow-lg transition-all disabled:opacity-40 disabled:shadow-none"
            >
              <Send className="h-3.5 w-3.5" />
              Publicar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
